import { combineReducers } from 'redux';

import * as types from 'modules/campaigns/actionTypes';
import { DASHBOARD_SOURCE } from 'modules/campaigns/constants';
import { MULTIPLE_FETCH_REQUEST } from 'modules/checklists/actionTypes';
import loading from 'modules/global/reducers/generic.loading';
import loadingArraySlice from 'modules/global/reducers/loadingArraySlice';
import createSearchReducers from 'modules/global/reducers/simpleSearchReducer';
import { LOCATION_CHANGE } from 'modules/router/actionTypes';

import { createReducer } from 'utils/ReduxUtils';

import creationModal from './creation-modal';
import duplicateModal from './duplicate-modal';
import filters from './filters';
import initialized from './initialized';
import sendModal from './send-modal';
import sendTestModal from './send-test-modal';

/**
 * @typedef {{
 *    campaignId: string,
 *    filters: import('modules/campaigns/reducers/ui/filters').CampaignsFiltersReducer,
 *    data: import('modules/global/reducers/simpleSearchReducer').SimpleSearchReducer,
 *    loading: {
 *      patch: string[],
 *      delete: string[],
 *    }
 * }} CampaignUIDashboardReducer
 */

/**
 * @typedef {{
 *   initialized: boolean,
 *   loading: {
 *     patch: boolean,
 *     save: boolean,
 *     delete: boolean,
 *   },
 *   campaignFiles: import('modules/global/reducers/simpleSearchReducer').SimpleSearchReducer,
 *   filters: import('modules/campaigns/reducers/ui/filters').filtersState,
 * }} CampaignUIDetailsReducer
 */

/**
 * @typedef {{
 *  sendTestModal: import('./send-test-modal').CampaignUIModalSendTestReducer,
 *  sendModal: import('./send-modal').CampaignUIModalSendReducer,
 *  duplicateModal: import('./duplicate-modal').CampaignUIModalDuplicateReducer,
 *  creationModal: import('./creation-modal').CampaignUIModalCreationReducer,
 *  dashboard: CampaignUIDashboardReducer,
 *  details: CampaignUIDetailsReducer,
 * }} CampaignUIReducer
 */

/**
 * @param {import("modules/global/generic.types").Action<unknown>} action
 * @returns {boolean}
 */
const dashboardPageActionMatcher = ({ meta = {} }) => meta.source === DASHBOARD_SOURCE;

const GENERIC_SLICE_CONFIG = {
  global: {
    matcher: dashboardPageActionMatcher,
    selectId: entity => entity?.id ?? entity?.campaignId ?? entity,
  },
};

export default combineReducers({
  sendModal,
  sendTestModal,
  creationModal,
  duplicateModal,
  dashboard: combineReducers({
    filters,
    data: createSearchReducers(types.FETCH_CAMPAIGNS, {
      ids: {
        [types.CREATE_CAMPAIGN_SUCCESS]: (state, { payload }) => [...state, payload.id],
        [types.DUPLICATE_CAMPAIGN_SUCCESS]: (state, { payload }) => [...state, payload.id],
      },
      global: { matcher: dashboardPageActionMatcher, paginationSlice: true },
    }),
    loading: combineReducers({
      delete: loadingArraySlice(types.DELETE_CAMPAIGN, GENERIC_SLICE_CONFIG),
      patch: loadingArraySlice(types.PATCH_CAMPAIGN_STATUS, GENERIC_SLICE_CONFIG),
    }),
    campaignId: createReducer(null, {
      [types.OPEN_MODAL_DUPLICATE]: (_, { payload: campaignId }) => campaignId,
      [types.CLOSE_MODAL_DUPLICATE]: () => null,
    }),
  }),
  details: combineReducers({
    initialized,
    export: combineReducers({
      campaignId: createReducer(null, {
        [types.OPEN_EXPORT]: (_, { payload: campaignId }) => campaignId,
        [types.CLOSE_EXPORT]: () => null,
        [types.EXPORT_CAMPAIGN_SUCCESS]: () => null,
      }),
    }),
    contentModalOpen: createReducer(false, {
      [types.OPEN_CONTENT_MODAL]: () => true,
      [types.CLOSE_CONTENT_MODAL]: () => false,
    }),
    loading: combineReducers({
      export: loading(types.EXPORT_CAMPAIGN),
      save: loading(types.UPDATE_CAMPAIGN),
      patchStatus: loading(types.PATCH_CAMPAIGN_STATUS),
      patchName: loading(types.PATCH_CAMPAIGN_NAME),
      delete: loading(types.DELETE_CAMPAIGN),
      follow: loading(types.FOLLOW_CAMPAIGN),
      unfollow: loading(types.UNFOLLOW_CAMPAIGN),
    }),
    preview: combineReducers({
      campaign: createReducer(null, {
        [types.FETCH_CAMPAIGN_PREVIEW_SUCCESS]: (_, { payload }) => payload.url,
        [LOCATION_CHANGE]: () => null,
      }),
    }),
    importChecklistsLoading: loading(MULTIPLE_FETCH_REQUEST, { global: { matcher: ({ meta }) => meta?.source === 'campaign-import' } }),
  }),
});
